// Port of ios/Sources/ViewModels/ScheduledPostPresentation.swift.
//
// What a scheduled message's STORED SERVER OBJECT says about its attachments.
//
// The store keeps the server's own JSON verbatim (`scheduled_posts.raw_json`) rather than a
// column per field, because `PUT /posts/schedule/{id}` refuses a partial body and a rebuilt
// object would silently drop whatever this client does not model. That decision is right and it
// leaves this gap: anything the UI wants to know beyond the few mapped columns has to be read
// back out of the raw object, and reading it in three places with three different guesses is how
// a list starts disagreeing with itself.
//
// So the reading lives here, once, and is tested — including the shapes a server can legitimately
// send: the key absent entirely (a message with no files), present and null, or present and
// empty. Pure. Kotlin twin: `ScheduledPostPresentation.kt`.

/**
 * The ids of the files the server is holding for this scheduled message, in order.
 *
 * An unparseable or unexpected body answers "no files" rather than throwing: this feeds a
 * list row and a resend, and neither is worth a crash over a field. The resend consequence is
 * the conservative one — a message sent without its attachment is visible and fixable, a
 * crash on the way to sending is not.
 */
export function fileIds(rawJson: string | null | undefined): string[] {
  if (rawJson == null || rawJson === '') return [];
  let object: unknown;
  try {
    object = JSON.parse(rawJson);
  } catch {
    return [];
  }
  if (object == null || typeof object !== 'object' || Array.isArray(object)) return [];
  const ids = (object as Record<string, unknown>).file_ids;
  if (!Array.isArray(ids)) return [];
  return ids.filter((id): id is string => typeof id === 'string' && id.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, '') !== '');
}

/** Whether the row should say it carries something besides words. */
export function hasAttachments(rawJson: string | null | undefined): boolean {
  return fileIds(rawJson).length > 0;
}
